import React, { useEffect, useMemo, useRef, useState } from "react";
import type { DraftSnapshot } from "../shared/draft";
import type { PricingItemRow } from "../shared/pricing";
import {
  isSenderInfoComplete,
  legacyProposalNotesStorageKey,
  proposalNotesStorageKey,
  proposalToggleStorageKey,
  readProposalToggle,
  resolveProposalLineItems,
  SENDER_INFO_MISSING_MESSAGE,
} from "../shared/proposalSend";
import {
  fetchProposalSections,
  type ProposalSection,
} from "../proposalSections";
import { fetchSowTemplatesRows } from "../sowTemplates";
import { filterSupersededSkirtingRows } from "../shared/format";
import {
  listVisualsForOrg,
  resolveProposalVisualsFrom,
  type ProposalVisual,
  type VisualLibraryRecord,
} from "../visualLibrary";
import MobileSendSheet from "./components/MobileSendSheet";
import {
  SPECIFIC_DATE_OPTION,
  defaultStartDate,
  formatStartDate,
  readStartDate,
  writeStartDate,
} from "../shared/timelineStartDate";
import { PERMIT_START_NOTE } from "../shared/permitStartNote";

/* ============================================================
   MobileProposalPage
   ----------------------------------------------------------------
   Mobile-friendly proposal review screen. Same data shape and
   section order as desktop's ProposalPage, but rendered as a
   scrollable card stack (no print tables, no print layout).

   Editable fields (live with the user's edits):
     - Scope of Work    → snapshot.sowText via onPatchSnapshot
     - Notes            → localStorage  du_proposal_notes::{est}
     - Start weeks      → localStorage  du_timeline_start_weeks::{est}
     - Duration days    → localStorage  du_timeline_duration_days::{est}
     - Start date       → localStorage  du_timeline_start_date::{est} (overrides weeks)
     - Show line prices → localStorage  du_show_line_prices::{est}

   The localStorage keys are the SAME ones desktop writes to, so
   buildProposalPayload (in src/shared/proposalSend.ts) reads them
   without any mobile-specific branch.

   Send wiring lives in MobileSendSheet — tapping "Send Proposal"
   in the footer opens the bottom-sheet send UI, which runs the same
   shared flow desktop's Email Proposal uses (buildProposalPayload →
   saveOrUpdateProposal → invokeSendProposalEmail).
   ============================================================ */

type Props = {
  snapshot: DraftSnapshot;
  pricingItems: PricingItemRow[];
  estimate: any;
  orgId: string | null;
  userSettings: any;
  onBack: () => void;
  /** Routes the user to the Settings tab. Used when the sender-info
   *  gate fires so they can fill in name / phone / email and retry. */
  onOpenSettings?: () => void;
  onPatchSnapshot: (patch: Partial<DraftSnapshot>) => void;
  onSend?: () => void;
};

// ----------------------------------------------------------------
// Helpers — mirror desktop ProposalPage's formatting
// ----------------------------------------------------------------

type ConstructionSowKey =
  | "new_construction"
  | "resurface"
  | "second_story"
  | "second_story_resurface"
  | "sleeper_system"
  | "second_story_sleeper";

function toSowKey(raw: string): ConstructionSowKey | "" {
  const v = (raw || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
  if (v.includes("second") && v.includes("resurface"))
    return "second_story_resurface";
  if (v.includes("second") && v.includes("sleeper"))
    return "second_story_sleeper";
  if (v.includes("second")) return "second_story";
  if (v.includes("sleeper")) return "sleeper_system";
  if (v.includes("resurface") || v.includes("redeck")) return "resurface";
  if (v.includes("new")) return "new_construction";
  return "";
}

/**
 * Auto-generated Project Description (the one-liner above Scope of Work).
 * Mirrors ProposalPage.buildProjectDescription so mobile and desktop seed the
 * SAME default from construction type + deck square footage. When the square
 * footage is missing/zero the "approximately N sq ft" clause is dropped.
 */
function buildProjectDescription(
  constructionType: string,
  sf: number | null | undefined
): string {
  const v = (constructionType || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

  let base: string;
  if (v.includes("second") && v.includes("sleeper"))
    base = "Design and construction of a new rooftop deck";
  else if (v.includes("second") && v.includes("resurface"))
    base = "Resurfacing of an existing second-story deck";
  else if (v.includes("second"))
    base = "Design and construction of a new second-story deck";
  else if (v.includes("sleeper"))
    base = "Design and construction of a new ground-level deck";
  else if (v.includes("resurface") || v.includes("redeck"))
    base = "Resurfacing of an existing deck";
  else if (v.includes("new"))
    base = "Design and construction of a new custom deck";
  else base = "Custom deck project";

  const n = Number(sf);
  const sfPart =
    Number.isFinite(n) && n > 0 ? `, approximately ${n} sq ft` : "";
  return `${base}${sfPart}`;
}

function money0(n: number) {
  return Math.round(n || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
}

function formatPhone(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function formatStartWeeksRange(weeks: number) {
  if (weeks <= 1) return "1 week";
  if (weeks === 2) return "2 weeks";
  if (weeks === 3) return "3 weeks";
  if (weeks === 4) return "4 weeks";
  if (weeks <= 6) return "5–6 weeks";
  if (weeks <= 8) return "7–8 weeks";
  return `${weeks} weeks`;
}

function formatDurationRange(days: number) {
  if (days <= 2) return "1–2 days";
  if (days <= 5) return "3–5 days";
  if (days <= 6) return "4–6 days";
  if (days <= 10) return "7–10 days";
  return "10+ days";
}

function fmtQty(
  qty?: number | null,
  unit?: string | null,
  name?: string | null,
  category?: string | null
) {
  const q = typeof qty === "number" && isFinite(qty) ? qty : null;
  if (q == null || q <= 0) return "";
  const qStr = Number.isInteger(q)
    ? String(q)
    : q.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const u0 = (unit || "").trim().toLowerCase();
  if (!u0) return qStr;
  if (u0 === "multiplier") {
    const n = (name || "").toLowerCase();
    const c = (category || "").toLowerCase();
    if (n.includes(" sf")) return `${qStr} SF`;
    if (n.includes(" lf")) return `${qStr} LF`;
    if (n.includes(" ea")) return `${qStr} EA`;
    if (c.includes("deck") || c.includes("skirting") || c.includes("lattice"))
      return `${qStr} SF`;
    if (c.includes("rail")) return `${qStr} LF`;
    return qStr;
  }
  return `${qStr} ${u0}`;
}

function bulletLines(text?: string | null) {
  return (text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

// Normalize legacy system layout ids the way ProposalPage does, so
// the user's saved proposalLayoutOrder works without a desktop edit.
function normalizeLayoutId(idRaw: any): string {
  const id = String(idRaw || "").trim();
  if (id === "_details_") return "__details__";
  if (id === "_timeline_") return "__timeline__";
  return id;
}

// ----------------------------------------------------------------
// Component
// ----------------------------------------------------------------

/**
 * Show Cost / Show Visuals for the CURRENT estimate on this screen. The value
 * carries the storage key it was read for; a different estimate reads its own
 * value, and a value is only ever written under its own key.
 */
function useEstimateToggle(
  name: "du_show_line_prices" | "du_show_visual_appendix",
  estimateId: string,
  estimateName: string
) {
  const key = proposalToggleStorageKey(name, estimateId, estimateName);
  const [entry, setEntry] = useState<{ key: string; on: boolean }>(() => ({
    key,
    on: readProposalToggle(name, estimateId, estimateName),
  }));
  useEffect(() => {
    setEntry((e) =>
      e.key === key
        ? e
        : { key, on: readProposalToggle(name, estimateId, estimateName) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const on =
    entry.key === key ? entry.on : readProposalToggle(name, estimateId, estimateName);
  useEffect(() => {
    if (entry.key !== key) return;
    try {
      localStorage.setItem(entry.key, entry.on ? "1" : "0");
    } catch {}
  }, [entry, key]);
  const setOn = (update: (v: boolean) => boolean) =>
    setEntry({ key, on: update(on) });
  return [on, setOn] as const;
}

export default function MobileProposalPage(props: Props) {
  const {
    snapshot,
    pricingItems,
    estimate,
    orgId,
    userSettings,
    onBack,
    onOpenSettings,
    onPatchSnapshot,
    onSend,
  } = props;

  // Send sheet open/close. Owned here so the proposal screen's edits
  // (SOW textarea, notes, timeline) flow into the payload at the moment
  // the user taps Send rather than at mount time.
  const [sendOpen, setSendOpen] = useState(false);

  // Read-only preview step. When true, every edit affordance hides and
  // the user sees the document exactly as the customer will (mirrors
  // desktop ProposalPage's readOnly prop). Footer flips to "Back to
  // edit" / "Send". Local state so SOW/Notes/Timeline edits survive
  // the trip back to edit mode without re-fetching.
  const [previewMode, setPreviewMode] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    // When the user enters preview, snap to the top of the document so
    // they see the masthead + Prepared By exactly like the customer
    // does on /review (not scrolled to wherever they were editing).
    if (previewMode && mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [previewMode]);

  const estimateName = snapshot.estimateName || "";
  const clientFirstName = snapshot.clientFirstName || "";
  const clientLastName = snapshot.clientLastName || "";
  const clientTown = snapshot.clientTown || "";
  const clientEmail = snapshot.clientEmail || "";
  const constructionType = snapshot.constructionType || "";
  const finalEstimate = Number(estimate?.finalEstimate) || 0;
  const upliftMultiplier = Number(estimate?.upliftMultiplier) || 1;

  // ------------- Resolved line items (same shape buildProposalPayload uses)
  const lineItems = useMemo(
    () =>
      resolveProposalLineItems(pricingItems, {
        selectedDeckingId: snapshot.selectedDeckingId || "",
        selectedRailingId: snapshot.selectedRailingId || "",
        selectedStairsId: snapshot.selectedStairsId || "",
        selectedFastenerId: snapshot.selectedFastenerId || "",
        selectedDemoId: snapshot.selectedDemoId || "",
        selectedSkirtingId: snapshot.selectedSkirtingId || "",
        deckingSqFt: Number(snapshot.deckingSqFt) || 0,
        railingLf: Number(snapshot.railingLf) || 0,
        stairsCount: Number(snapshot.stairsCount) || 0,
        fastenerQtyAuto: Number(estimate?.fastenerQtyAuto) || 0,
        skirtingSf: Number(snapshot.skirtingSf) || 0,
      }),
    [pricingItems, snapshot, estimate?.fastenerQtyAuto]
  );

  // ------------- Proposal toggles (Show Cost / Show Visuals)
  // Keyed through the shared helper on estimateId || estimateName, so this
  // screen, the desktop proposal screen and the send-time snapshot builder all
  // read and write the SAME slot. Keying on the name alone put every unnamed
  // estimate on one shared key.
  const toggleEstimateId = (snapshot.estimateId || "").trim();

  // Each toggle carries the storage key (i.e. the estimate) it belongs to.
  // This screen stays mounted and a review of ANOTHER estimate reuses it; the
  // toggles used to be read once at mount and then written under whatever
  // key was current, so reviewing B after A saved A's settings over B's. A
  // different key now reads that estimate's own value, and a value is only
  // written under its own key.
  const [showLineItemPrices, setShowLineItemPrices] = useEstimateToggle(
    "du_show_line_prices",
    toggleEstimateId,
    estimateName
  );

  // ------------- Show Visual Appendix toggle (default OFF)
  const [showVisualAppendix, setShowVisualAppendix] = useEstimateToggle(
    "du_show_visual_appendix",
    toggleEstimateId,
    estimateName
  );

  // ------------- Timeline (start weeks / duration days)
  //
  // This screen stays mounted once opened, and a review of ANOTHER estimate
  // reuses it — so the values must follow the current estimate. They used to
  // be read once at mount and written back whenever the name changed, so
  // reviewing B after A showed A's Timeline and saved it over B's real value.
  // `owner` (the estimate id, else the name) says which estimate the values
  // belong to; a different id loads that estimate's own values, a rename
  // (same id) keeps them. Same rule as the desktop ProposalPage.
  const timelineOwner =
    (snapshot.estimateId || "").trim() || `name:${estimateName}`;
  const timelineOwnerRef = useRef(timelineOwner);
  timelineOwnerRef.current = timelineOwner;
  const timelineNameRef = useRef(estimateName);
  timelineNameRef.current = estimateName;
  type TimelineState = { owner: string; start: number; dur: number; startDate: string };
  const readTimeline = (owner: string, name: string): TimelineState => {
    const num = (key: string, fallback: number) => {
      try {
        const n = Number(localStorage.getItem(key));
        return Number.isFinite(n) && n >= 1 ? n : fallback;
      } catch {
        return fallback;
      }
    };
    return {
      owner,
      start: num(`du_timeline_start_weeks::${name}`, 3),
      dur: num(`du_timeline_duration_days::${name}`, 2),
      startDate: readStartDate(name),
    };
  };
  const [timelineState, setTimelineState] = useState<TimelineState>(() =>
    readTimeline(timelineOwner, estimateName)
  );
  const timeline =
    timelineState.owner === timelineOwner
      ? timelineState
      : readTimeline(timelineOwner, estimateName);
  useEffect(() => {
    setTimelineState((t) =>
      t.owner === timelineOwner ? t : readTimeline(timelineOwner, estimateName)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineOwner]);
  const startWeeks = timeline.start;
  const durationDays = timeline.dur;
  const startDate = timeline.startDate;
  const updateTimeline = (
    patch: Partial<Pick<TimelineState, "start" | "dur" | "startDate">>
  ) =>
    setTimelineState((t) => {
      const base =
        t.owner === timelineOwnerRef.current
          ? t
          : readTimeline(timelineOwnerRef.current, timelineNameRef.current);
      return { ...base, ...patch };
    });
  const setStartWeeks = (v: number) => updateTimeline({ start: v, startDate: "" });
  const setStartDate = (v: string) => updateTimeline({ startDate: v });
  const setDurationDays = (v: number) => updateTimeline({ dur: v });
  useEffect(() => {
    // Only the current estimate's own values are saved.
    if (timelineState.owner !== timelineOwner) return;
    try {
      localStorage.setItem(
        `du_timeline_start_weeks::${estimateName}`,
        String(timelineState.start)
      );
      localStorage.setItem(
        `du_timeline_duration_days::${estimateName}`,
        String(timelineState.dur)
      );
      writeStartDate(estimateName, timelineState.startDate);
    } catch {}
  }, [timelineState, timelineOwner, estimateName]);

  // ------------- Notes (du_proposal_notes::*)
  // Keyed on the STABLE estimate id, same as desktop and same as what
  // buildProposalPayload reads. It used to prefer estimateName, which mobile
  // never sets — and a blank name yields the degenerate key every unnamed
  // estimate shares, so notes leaked between estimates. The name key is now
  // read-only legacy, and only when the name is non-empty.
  const notesKey = proposalNotesStorageKey((snapshot.estimateId || "").trim());
  const legacyNotesKey = legacyProposalNotesStorageKey(estimateName || "");
  // `key` says which estimate the text in state belongs to. This screen stays
  // mounted and a review of ANOTHER estimate reuses it: the text used to be
  // read once at mount and then written under whatever notesKey was current,
  // so reviewing B after A showed A's Notes and saved them over B's. Now a
  // different key reads that estimate's own Notes, and text is only ever
  // written under the key it belongs to (same shape as desktop's notesEntry).
  const readNotes = (key: string): string => {
    try {
      if (!key) return "";
      return (
        localStorage.getItem(key) ||
        (legacyNotesKey ? localStorage.getItem(legacyNotesKey) : "") ||
        ""
      );
    } catch {
      return "";
    }
  };
  const [notesEntry, setNotesEntry] = useState<{ key: string; text: string }>(
    () => ({ key: notesKey, text: readNotes(notesKey) })
  );
  const proposalNotes =
    notesEntry.key === notesKey ? notesEntry.text : readNotes(notesKey);
  const setProposalNotes = (text: string) =>
    setNotesEntry({ key: notesKey, text });
  useEffect(() => {
    setNotesEntry((e) =>
      e.key === notesKey ? e : { key: notesKey, text: readNotes(notesKey) }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesKey]);
  useEffect(() => {
    if (!notesEntry.key || notesEntry.key !== notesKey) return;
    try {
      localStorage.setItem(notesEntry.key, notesEntry.text || "");
    } catch {}
  }, [notesKey, notesEntry]);

  // ------------- SOW: auto template + live override on the snapshot
  const sowKey = useMemo(() => toSowKey(constructionType), [constructionType]);
  const [sowTemplatesMap, setSowTemplatesMap] = useState<
    Record<string, string>
  >(() => {
    try {
      return JSON.parse(localStorage.getItem("du_sow_templates_v1") || "{}");
    } catch {
      return {};
    }
  });
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchSowTemplatesRows();
        const map: Record<string, string> = {};
        rows.forEach((r) => {
          map[r.construction_key] = r.body || "";
        });
        setSowTemplatesMap(map);
      } catch {
        // Keep whatever's cached.
      }
    })();
  }, []);

  const autoScopeText = useMemo(
    () => (sowKey ? String(sowTemplatesMap?.[sowKey] || "") : ""),
    [sowKey, sowTemplatesMap]
  );

  // Same persisted touched-flag model as desktop ProposalPage: untouched seeds
  // the live template, but once edited the raw stored value wins (empty stays
  // empty). Shares snapshot.sowTouched so a clear/custom SOW is consistent
  // across desktop and mobile.
  const liveSowText = snapshot.sowText ?? "";
  const sowTextValue = snapshot.sowTouched
    ? liveSowText
    : liveSowText || autoScopeText;
  const handleSowChange = (next: string) => {
    onPatchSnapshot({ sowText: next, sowTouched: true });
  };

  // ------------- Project Description: same auto-fill-but-editable model as SOW
  // (and as desktop ProposalPage). Untouched shows the generated one-liner;
  // once edited, the stored value wins (empty stays empty). Round-trips via
  // snapshot.projectDescription / projectDescriptionTouched.
  const autoProjectDescription = useMemo(
    () =>
      buildProjectDescription(constructionType, Number(snapshot.deckingSqFt) || 0),
    [constructionType, snapshot.deckingSqFt]
  );
  const liveProjectDescription = snapshot.projectDescription ?? "";
  const projectDescriptionValue = snapshot.projectDescriptionTouched
    ? liveProjectDescription
    : liveProjectDescription || autoProjectDescription;
  const handleProjectDescriptionChange = (next: string) => {
    onPatchSnapshot({
      projectDescription: next,
      projectDescriptionTouched: true,
    });
  };

  // ------------- Custom org sections (bullets / paragraph)
  const [dbProposalSections, setDbProposalSections] = useState<
    ProposalSection[]
  >([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orgId) {
        setDbProposalSections([]);
        return;
      }
      try {
        const rows = await fetchProposalSections(orgId);
        if (!alive) return;
        setDbProposalSections(Array.isArray(rows) ? rows : []);
      } catch {
        if (!alive) return;
        setDbProposalSections([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId]);

  const enabledSections = useMemo(
    () => (dbProposalSections || []).filter((s) => s?.enabled),
    [dbProposalSections]
  );

  // ------------- Effective render order — same algorithm as ProposalPage
  const layoutOrder = useMemo<string[]>(() => {
    const arr = (userSettings as any)?.proposalLayoutOrder;
    return Array.isArray(arr)
      ? arr
          .map(normalizeLayoutId)
          .map((x) => String(x).trim())
          .filter(Boolean)
      : [];
  }, [userSettings]);

  const effectiveOrder = useMemo<string[]>(() => {
    const enabledCustomIds = enabledSections.map((s) => String(s.id).trim());
    const raw = (layoutOrder || []).map((x) => String(x).trim()).filter(Boolean);
    const filtered = raw.filter(
      (id) =>
        id === "__details__" ||
        id === "__timeline__" ||
        enabledCustomIds.includes(id)
    );
    const missingCustom = enabledCustomIds.filter(
      (id) => !filtered.includes(id)
    );
    let out = [...filtered, ...missingCustom];
    if (!out.includes("__details__")) out.push("__details__");
    if (!out.includes("__timeline__")) out.push("__timeline__");
    out = out.filter((v, i) => out.indexOf(v) === i);
    return out;
  }, [layoutOrder, enabledSections]);

  // ------------- Detail rows + optional rows (same logic as ProposalPage)
  const lineTotal = (baseCost: number, isFixedPrice?: boolean) =>
    (baseCost || 0) * (isFixedPrice ? 1 : upliftMultiplier || 1);

  const addItemsDetailed = useMemo(
    () => (Array.isArray(estimate?.addItemsDetailed) ? estimate.addItemsDetailed : []),
    [estimate?.addItemsDetailed]
  );

  const rows = useMemo(() => {
    // Print ONLY the chosen skirting category, never the combined string.
    // ("Skirting" and "Lattice" are distinct stored values; the table used to
    // hardcode "Skirting / Lattice".) Fall back to the combined label only when
    // the category is genuinely unknown — never assert the wrong single value.
    const skirtCat = (snapshot.skirtingCategory || "").trim().toLowerCase();
    const skirtingLabel =
      skirtCat === "lattice"
        ? "Lattice"
        : skirtCat === "skirting"
          ? "Skirting"
          : "Skirting / Lattice";

    const baseRows: Array<{
      key: string;
      label: string;
      typeText?: string;
      qtyText: string;
      description: string;
      baseCost: number;
      isFixedPrice?: boolean;
    }> = [
      {
        key: "decking",
        label: "Decking",
        typeText: lineItems.deckingType || "",
        qtyText: fmtQty(
          lineItems.deckingQty,
          lineItems.deckingUnit,
          lineItems.deckingType,
          "Decking"
        ),
        description:
          (lineItems.deckingDescription || "").trim() ||
          "Composite decking installed to match selected finish and layout.",
        baseCost: Number(estimate?.deckingSubtotal) || 0,
      },
      {
        key: "railing",
        label: "Railing",
        typeText: lineItems.railingType || "",
        qtyText: fmtQty(lineItems.railingQty, lineItems.railingUnit),
        description:
          (lineItems.railingDescription || "").trim() ||
          "Railing system installed per code with a durable finish.",
        baseCost: Number(estimate?.railingSubtotal) || 0,
      },
      {
        key: "stairs",
        label: "Stairs",
        typeText: lineItems.stairsType || "",
        qtyText: fmtQty(lineItems.stairsQty, lineItems.stairsUnit),
        description:
          (lineItems.stairsDescription || "").trim() ||
          "Stair system built for safe access with consistent rise/run.",
        baseCost: Number(estimate?.stairsSubtotal) || 0,
      },
      {
        key: "fasteners",
        label: "Fasteners",
        typeText: lineItems.fastenerType || "",
        qtyText: fmtQty(lineItems.fastenerQty, lineItems.fastenerUnit),
        description:
          (lineItems.fastenerDescription || "").trim() ||
          "Fasteners and hardware required for proper installation.",
        baseCost: Number(estimate?.fastenerSubtotal) || 0,
      },
      {
        key: "demo",
        label: "Demolition",
        typeText: lineItems.demoType || "",
        qtyText: fmtQty(Number(snapshot.demoQty) || 0, "ea"),
        description:
          (lineItems.demoDescription || "").trim() ||
          "Removal and disposal of existing materials as required.",
        baseCost: Number(estimate?.demoSubtotal) || 0,
      },
      {
        key: "skirting",
        label: skirtingLabel,
        typeText: lineItems.skirtingType || "",
        qtyText: fmtQty(lineItems.skirtingQty, lineItems.skirtingUnit),
        description:
          (lineItems.skirtingDescription || "").trim() ||
          "Finished skirting/lattice to close off underside cleanly.",
        baseCost: Number(estimate?.skirtingSubtotal) || 0,
      },
    ];

    const addOns = (addItemsDetailed || [])
      .filter((r: any) => {
        if (r?.optional) return false;
        const base = Number(r?.lineBase || 0);
        const hasPicked = !!r?.picked?.name;
        const hasCustomName = !!r?.customName?.toString().trim();
        const hasCustomDesc = !!r?.customDescription?.toString().trim();
        return base !== 0 && (hasPicked || hasCustomName || hasCustomDesc);
      })
      .map((r: any) => {
        const customName = r?.customName?.toString().trim() || "";
        const customDesc = r?.customDescription?.toString().trim() || "";
        const isMisc = (r.category || "").toLowerCase().trim() === "misc";
        const typeText =
          customName || r.picked?.name || (isMisc ? "Misc Item" : "");
        const baseDesc =
          customDesc ||
          r.picked?.proposal_description?.toString().trim() ||
          r.picked?.description?.toString().trim() ||
          (isMisc
            ? ""
            : "Additional feature included as selected in the project scope.");
        const benchType = r?.benchType || "";
        const benchSuffix = benchType.includes("_back")
          ? " Includes backrest."
          : benchType.includes("_storage")
            ? " Includes integrated storage."
            : "";
        const description =
          (r.category || "").toLowerCase().trim() === "bench" && baseDesc
            ? baseDesc + benchSuffix
            : baseDesc;
        return {
          key: `add-${r.rowId}`,
          label: (r.category || "Additional Item").toString(),
          typeText,
          qtyText: isMisc
            ? r?.customQtyText?.toString().trim() || "—"
            : fmtQty(
                r.qty,
                r.unit,
                customName || r.picked?.name || "",
                r.category || ""
              ),
          description,
          baseCost: Number(r.lineBase || 0),
          isFixedPrice: r.isFixedPrice ?? isMisc,
        };
      });

    const priced = [...baseRows, ...addOns].filter(
      (r) => (r.baseCost || 0) !== 0
    );

    // Display only — horizontal skirting supersedes the vertical row.
    // Costs are already summed upstream, so the total is unchanged.
    return filterSupersededSkirtingRows(priced, {
      skirtingCategory: snapshot.skirtingCategory,
      skirtingType: lineItems.skirtingType,
    });
  }, [
    lineItems,
    estimate?.deckingSubtotal,
    estimate?.railingSubtotal,
    estimate?.stairsSubtotal,
    estimate?.fastenerSubtotal,
    estimate?.demoSubtotal,
    estimate?.skirtingSubtotal,
    snapshot.demoQty,
    snapshot.skirtingCategory,
    addItemsDetailed,
  ]);

  const optionalRows = useMemo(() => {
    return (addItemsDetailed || [])
      .filter((r: any) => {
        if (!r?.optional) return false;
        const base = Number(r?.lineBase || 0);
        const hasPicked = !!r?.picked?.name;
        const hasCustomName = !!r?.customName?.toString().trim();
        const hasCustomDesc = !!r?.customDescription?.toString().trim();
        return base !== 0 && (hasPicked || hasCustomName || hasCustomDesc);
      })
      .map((r: any) => {
        const customName = r?.customName?.toString().trim() || "";
        const customDesc = r?.customDescription?.toString().trim() || "";
        const isMisc = (r.category || "").toLowerCase().trim() === "misc";
        const typeText =
          customName || r.picked?.name || (isMisc ? "Misc Item" : "");
        const baseDesc =
          customDesc ||
          r.picked?.proposal_description?.toString().trim() ||
          r.picked?.description?.toString().trim() ||
          (isMisc
            ? ""
            : "Additional feature included as selected in the project scope.");
        const benchType = r?.benchType || "";
        const benchSuffix = benchType.includes("_back")
          ? " Includes backrest."
          : benchType.includes("_storage")
            ? " Includes integrated storage."
            : "";
        const description =
          (r.category || "").toLowerCase().trim() === "bench" && baseDesc
            ? baseDesc + benchSuffix
            : baseDesc;
        return {
          key: `opt-${r.rowId}`,
          label: (r.category || "Additional Item").toString(),
          typeText,
          qtyText: isMisc
            ? r?.customQtyText?.toString().trim() || "—"
            : fmtQty(
                r.qty,
                r.unit,
                customName || r.picked?.name || "",
                r.category || ""
              ),
          description,
          baseCost: Number(r.lineBase || 0),
          isFixedPrice: r.isFixedPrice ?? isMisc,
        };
      });
  }, [addItemsDetailed]);

  // ------------- Visual appendix.
  // Resolve the matched product photos from the org's visual library for the
  // CURRENT selections — the SAME logic desktop uses (shared
  // resolveProposalVisualsFrom). Mobile-created proposals never had
  // snapshot.visuals populated, so "Show Visuals" showed nothing and the
  // customer's /review page had no appendix; resolving here fixes both.
  const [visualRecords, setVisualRecords] = useState<VisualLibraryRecord[]>([]);
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    listVisualsForOrg(orgId)
      .then((rows) => {
        if (alive) setVisualRecords(rows);
      })
      .catch(() => {
        /* non-fatal: proposal simply renders no appendix */
      });
    return () => {
      alive = false;
    };
  }, [orgId]);

  const resolvedVisuals = useMemo<ProposalVisual[]>(() => {
    if (!visualRecords.length) return [];
    const find = (id: string) => {
      if (!id) return null;
      const r = pricingItems.find((pp) => String(pp.id) === String(id));
      return r ? { id: (r as any).id, name: (r as any).name } : null;
    };
    // Stairs in the job: main Stairs configured OR a "Stair options" add-item.
    const stairsPresent =
      (!!snapshot.selectedStairsId && Number(snapshot.stairsCount) > 0) ||
      addItemsDetailed.some(
        (r: any) =>
          (r?.category || "")
            .toLowerCase()
            .trim()
            .replace(/[\s-]+/g, "_") === "stair_options"
      );
    const demoPresent =
      !!snapshot.selectedDemoId || Number(snapshot.demoQty) > 0;
    // A Gate add-item present → gate photo (keyed to the selected railing).
    const gatePresent = addItemsDetailed.some(
      (r: any) =>
        (r?.category || "")
          .toLowerCase()
          .trim()
          .replace(/[\s-]+/g, "_") === "gate"
    );
    return resolveProposalVisualsFrom(visualRecords, {
      decking: find(snapshot.selectedDeckingId || ""),
      railing: find(snapshot.selectedRailingId || ""),
      skirting: find(snapshot.selectedSkirtingId || ""),
      fastener: find(snapshot.selectedFastenerId || ""),
      skirtingCategory: snapshot.skirtingCategory || "",
      addItems: addItemsDetailed,
      stairsPresent,
      demoPresent,
      gatePresent,
    });
  }, [
    visualRecords,
    pricingItems,
    snapshot.selectedDeckingId,
    snapshot.selectedRailingId,
    snapshot.selectedSkirtingId,
    snapshot.selectedFastenerId,
    snapshot.skirtingCategory,
    snapshot.selectedStairsId,
    snapshot.stairsCount,
    snapshot.selectedDemoId,
    snapshot.demoQty,
    addItemsDetailed,
  ]);

  // Prefer freshly-resolved matches; fall back to any visuals already embedded
  // in the snapshot (e.g. a draft last saved on desktop) if the fetch found
  // none. Also feeds the send payload so the sent proposal + /review carry it.
  const effectiveVisuals: ProposalVisual[] = resolvedVisuals.length
    ? resolvedVisuals
    : ((snapshot.visuals || []) as ProposalVisual[]);

  const proposalVisuals = useMemo(() => {
    return effectiveVisuals
      .filter((v) => v.image_url?.trim())
      .map((v, i) => ({
        id: `${v.category}-${i}`,
        category: v.category,
        displayName: v.label,
        imageRef: v.image_url,
        caption: v.caption || "",
      }));
  }, [effectiveVisuals]);

  // Snapshot handed to the send sheet, with the resolved visuals embedded so
  // buildProposalPayload freezes them into the proposals row (customer /review).
  const snapshotForSend = useMemo(
    () => ({ ...snapshot, visuals: effectiveVisuals }),
    [snapshot, effectiveVisuals]
  );

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  const renderDetailsCard = () => (
    <section className="m-proposal-card" key="__details__">
      <header className="m-proposal-card-head">
        <h3 className="m-proposal-card-title">Details</h3>
        {/* Show Cost / Show Visuals toggles moved to the sticky top toolbar
            (just under the header) so they're reachable without scrolling. */}
      </header>

      {rows.length === 0 ? (
        <p className="m-proposal-empty">No items selected yet.</p>
      ) : (
        <ul className="m-proposal-line-list">
          {rows.map((r) => (
            <li className="m-proposal-line" key={r.key}>
              <div className="m-proposal-line-head">
                <div className="m-proposal-line-title">
                  {/* Category bold on top, material gray below (misc keeps name). */}
                  <div className="m-proposal-line-name">
                    {r.label && r.label.toLowerCase() !== "misc"
                      ? r.label
                      : r.typeText || r.label}
                  </div>
                  {r.label &&
                  r.label.toLowerCase() !== "misc" &&
                  r.typeText ? (
                    <div className="m-proposal-line-sub">{r.typeText}</div>
                  ) : null}
                </div>
                {showLineItemPrices && (
                  <div className="m-proposal-line-price">
                    {money0(lineTotal(r.baseCost, r.isFixedPrice))}
                  </div>
                )}
              </div>
              <div className="m-proposal-line-meta">
                <span className="m-proposal-line-qty">{r.qtyText || "—"}</span>
                {r.description ? (
                  <p className="m-proposal-line-desc">{r.description}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="m-proposal-total-row">
        <span className="m-proposal-total-label">Total Investment</span>
        <span className="m-proposal-total-amount">{money0(finalEstimate)}</span>
      </div>
    </section>
  );

  const renderTimelineCard = () => (
    <section className="m-proposal-card" key="__timeline__">
      <h3 className="m-proposal-card-title">Timeline</h3>
      {previewMode ? null : (
        <div className="m-proposal-timeline-grid">
          <label className="m-field">
            <span className="m-section-label">Est. Start Date</span>
            <select
              className="m-select"
              value={startDate ? SPECIFIC_DATE_OPTION : startWeeks}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v === SPECIFIC_DATE_OPTION) {
                  if (!startDate) setStartDate(defaultStartDate(startWeeks));
                } else setStartWeeks(v);
              }}
            >
              <option value={1}>1 week</option>
              <option value={2}>2 weeks</option>
              <option value={3}>3 weeks</option>
              <option value={4}>4 weeks</option>
              <option value={6}>5–6 weeks</option>
              <option value={8}>7–8 weeks</option>
              <option value={SPECIFIC_DATE_OPTION}>Specific date…</option>
            </select>
            {startDate ? (
              <input
                type="date"
                className="m-select"
                aria-label="Start date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            ) : null}
          </label>
          <label className="m-field">
            <span className="m-section-label">Est. Project Duration</span>
            <select
              className="m-select"
              value={durationDays}
              onChange={(e) => setDurationDays(Number(e.target.value))}
            >
              <option value={2}>1–2 days</option>
              <option value={5}>3–5 days</option>
              <option value={6}>4–6 days</option>
              <option value={10}>7–10 days</option>
              <option value={14}>10+ days</option>
            </select>
          </label>
        </div>
      )}
      <p className="m-proposal-timeline-summary">
        {startDate
          ? `Estimated start: ${formatStartDate(startDate)}.`
          : `Estimated start timeframe: ${formatStartWeeksRange(startWeeks)}.`}{" "}
        Estimated project duration: {formatDurationRange(durationDays)}.
      </p>
    </section>
  );

  const renderCustomSection = (sec: ProposalSection) => (
    <section className="m-proposal-card" key={sec.id}>
      <h3 className="m-proposal-card-title">{sec.title}</h3>
      {sec.type === "bullets" ? (
        <ul className="m-proposal-bullets">
          {bulletLines(sec.text).map((line, idx) => (
            <li key={`${sec.id}_${idx}`}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className="m-proposal-text m-proposal-pre">{sec.text || ""}</p>
      )}
    </section>
  );

  return (
    <>
      {/* Sticky top toolbar — Show Cost / Show Visuals, right under the
          "for <name> — <town>" header. Edit mode only (the toggles don't
          apply in the read-only preview). */}
      {previewMode ? null : (
        <div className="m-proposal-toolbar" role="group" aria-label="Proposal options">
          <button
            type="button"
            className={`m-prop-toggle ${showLineItemPrices ? "is-on" : ""}`}
            onClick={() => setShowLineItemPrices((v) => !v)}
            aria-pressed={showLineItemPrices}
          >
            {showLineItemPrices ? "Hide Cost" : "Show Cost"}
          </button>
          <button
            type="button"
            className={`m-prop-toggle ${showVisualAppendix ? "is-on" : ""}`}
            onClick={() => setShowVisualAppendix((v) => !v)}
            aria-pressed={showVisualAppendix}
          >
            {showVisualAppendix ? "Hide Visuals" : "Show Visuals"}
          </button>
        </div>
      )}

      <main
        ref={mainRef}
        className={`m-content m-proposal ${previewMode ? "is-preview" : ""}`}
        aria-label={previewMode ? "Proposal preview (read-only)" : "Proposal"}
      >
      {/* Read-only preview banner. Communicates the "this is what they'll
          see" mode and gives the user a second confirmation moment before
          the email actually goes out. */}
      {previewMode ? (
        <div className="m-proposal-preview-banner" role="status">
          Preview — this is what {clientLastName ? `${clientLastName} ` : "your client "}
          will see. Tap Send to email it.
        </div>
      ) : null}

      {/* Header summary card */}
      <section className="m-proposal-card m-proposal-summary">
        <div className="m-proposal-summary-logo">
          {userSettings?.logoDataUrl ? (
            <img
              src={userSettings.logoDataUrl}
              alt="Company Logo"
              className="m-proposal-logo-img"
            />
          ) : (
            <div className="m-proposal-logo-fallback">LOGO</div>
          )}
          {userSettings?.logoSlogan?.trim() ? (
            <div className="m-proposal-logo-slogan">
              {userSettings.logoSlogan.trim()}
            </div>
          ) : null}
        </div>

        <h2 className="m-proposal-title">Project Estimate</h2>

        <div className="m-proposal-summary-grid">
          <div className="m-proposal-summary-block">
            <div className="m-proposal-summary-h">Prepared by</div>
            <div className="m-proposal-summary-l">
              {userSettings?.userName || "—"}
            </div>
            <div className="m-proposal-summary-l m-proposal-muted">
              {formatPhone(userSettings?.userPhone || "") || "—"}
            </div>
            <div className="m-proposal-summary-l m-proposal-muted">
              {userSettings?.userEmail || "—"}
            </div>
          </div>
          <div className="m-proposal-summary-block">
            <div className="m-proposal-summary-h">Client</div>
            <div className="m-proposal-summary-l">
              {[clientFirstName, clientLastName]
                .map((s) => s.trim())
                .filter(Boolean)
                .join(" ") || "—"}
            </div>
            <div className="m-proposal-summary-l m-proposal-muted">
              {clientTown || "—"}
            </div>
            <div className="m-proposal-summary-l m-proposal-muted">
              {clientEmail || "—"}
            </div>
            <div className="m-proposal-summary-l m-proposal-muted">
              {new Date().toLocaleDateString([], {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Project Description — one-liner above Scope of Work. Editable in edit
          mode, plain text in preview. Mirrors the desktop proposal. */}
      <section className="m-proposal-card">
        <h3 className="m-proposal-card-title">Project Description</h3>
        {previewMode ? (
          <p className="m-proposal-text m-proposal-pre">
            {projectDescriptionValue || ""}
          </p>
        ) : (
          <textarea
            className="m-proposal-textarea"
            value={projectDescriptionValue}
            onChange={(e) => handleProjectDescriptionChange(e.target.value)}
            rows={3}
            spellCheck
            autoCorrect="on"
            autoCapitalize="sentences"
            placeholder={
              autoProjectDescription
                ? ""
                : "One-line summary — pick a Construction Type to seed it."
            }
          />
        )}
      </section>

      {/* Scope of Work — editable in edit mode, plain text in preview */}
      <section className="m-proposal-card">
        <h3 className="m-proposal-card-title">Scope of Work</h3>
        {previewMode ? (
          <p className="m-proposal-text m-proposal-pre">
            {sowTextValue || ""}
          </p>
        ) : (
          <textarea
            className="m-proposal-textarea"
            value={sowTextValue}
            onChange={(e) => handleSowChange(e.target.value)}
            rows={8}
            spellCheck
            autoCorrect="on"
            autoCapitalize="sentences"
            placeholder={
              autoScopeText
                ? ""
                : "Describe the work — pick a Construction Type to seed a template."
            }
          />
        )}
      </section>

      {/* Render in SettingsPage order, mixing system blocks + custom sections */}
      {effectiveOrder.map((idRaw) => {
        const id = normalizeLayoutId(idRaw);
        if (id === "__details__") return renderDetailsCard();
        if (id === "__timeline__") return renderTimelineCard();
        const sec = enabledSections.find(
          (s) => String(s.id).trim() === String(id).trim()
        );
        if (!sec) return null;
        return renderCustomSection(sec);
      })}

      {/* Optional Add-Ons */}
      {optionalRows.length > 0 ? (
        <section className="m-proposal-card">
          <h3 className="m-proposal-card-title">Optional Add-Ons</h3>
          <p className="m-proposal-text">
            Enhance your project with any of the options below.
          </p>
          <ul className="m-proposal-line-list">
            {optionalRows.map((r: any) => (
              <li className="m-proposal-line" key={r.key}>
                <div className="m-proposal-line-head">
                  <div className="m-proposal-line-title">
                    {/* Category bold on top, material gray below (misc keeps name). */}
                    <div className="m-proposal-line-name">
                      {r.label && r.label.toLowerCase() !== "misc"
                        ? r.label
                        : r.typeText || r.label}
                    </div>
                    {r.label &&
                    r.label.toLowerCase() !== "misc" &&
                    r.typeText ? (
                      <div className="m-proposal-line-sub">{r.typeText}</div>
                    ) : null}
                  </div>
                  <div className="m-proposal-line-price">
                    {money0(lineTotal(r.baseCost, r.isFixedPrice))}
                  </div>
                </div>
                <div className="m-proposal-line-meta">
                  <span className="m-proposal-line-qty">
                    {r.qtyText || "—"}
                  </span>
                  {r.description ? (
                    <p className="m-proposal-line-desc">{r.description}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Notes — editable in edit mode, plain text in preview. In preview
          mirrors desktop ProposalPage (line 1403): only the text shows
          under the heading; if empty the heading still renders so the
          layout matches the customer's /review experience. */}
      <section className="m-proposal-card">
        <h3 className="m-proposal-card-title">Notes</h3>
        {snapshot.includePermit ? (
          <p className="m-proposal-text">{PERMIT_START_NOTE}</p>
        ) : null}
        {previewMode ? (
          proposalNotes.trim() ? (
            <p className="m-proposal-text m-proposal-pre">{proposalNotes}</p>
          ) : null
        ) : (
          <textarea
            className="m-proposal-textarea"
            value={proposalNotes}
            onChange={(e) => setProposalNotes(e.target.value)}
            rows={4}
            placeholder="Notes (optional)…"
            spellCheck
            autoCorrect="on"
            autoCapitalize="sentences"
          />
        )}
      </section>

      {/* Visual Appendix */}
      {showVisualAppendix && proposalVisuals.length > 0 ? (
        <section className="m-proposal-card">
          <h3 className="m-proposal-visual-title">Visual Appendix</h3>
          <p className="m-proposal-visual-subtitle">
            Photo examples are for visual reference only. Actual colors and
            styles may vary.
          </p>
          <div className="m-proposal-visual-grid">
            {proposalVisuals.map((record) => (
              <figure key={record.id} className="m-proposal-visual-card">
                <img
                  src={record.imageRef}
                  alt={record.displayName}
                  className="m-proposal-visual-image"
                />
                <figcaption className="m-proposal-visual-label">
                  {(record.category || "").toLowerCase().includes("skirt") ? (
                    <strong>Skirting</strong>
                  ) : (
                    <>
                      <span className="m-proposal-muted">
                        {(record.category || "").toLowerCase().includes("latt")
                          ? "Lattice"
                          : record.category}{" "}
                        —{" "}
                      </span>
                      <strong>{record.displayName}</strong>
                    </>
                  )}
                </figcaption>
                {record.caption?.trim() ? (
                  <div className="m-proposal-visual-caption">
                    {record.caption}
                  </div>
                ) : null}
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      </main>

      {/* Footer actions — sticky bottom bar, ALWAYS visible (no scrolling to
          the bottom to reach them). Labels flip with previewMode.
          Edit mode: Back to Estimator / Send Proposal (→ enters preview).
          Preview:   Back to edit       / Send         (→ opens send sheet). */}
      <div className="m-proposal-footer">
        {previewMode ? (
          <>
            <button
              type="button"
              className="m-save-btn"
              onClick={() => setPreviewMode(false)}
            >
              Back to edit
            </button>
            <button
              type="button"
              className="m-review-btn"
              onClick={() => setSendOpen(true)}
            >
              Send
            </button>
          </>
        ) : (
          <>
            <button type="button" className="m-save-btn" onClick={onBack}>
              Back to Estimator
            </button>
            <button
              type="button"
              className="m-review-btn"
              onClick={() => {
                // Optional external hook still fires for any parent that
                // wants to observe the tap (legacy callers).
                if (onSend) onSend();
                // Sender-info gate. Blank user_name / user_phone /
                // user_email would mean the customer sees a half-filled
                // signature and the edge function has no reply-to, so
                // route the user to Settings before letting them preview.
                if (!isSenderInfoComplete(userSettings)) {
                  alert(SENDER_INFO_MISSING_MESSAGE);
                  if (onOpenSettings) onOpenSettings();
                  else onBack();
                  return;
                }
                if (!(clientEmail || "").trim()) {
                  // No client email — bounce the user back to the
                  // estimator's Customer card so they can fix it, rather
                  // than letting them stage a preview they can't send.
                  alert(
                    "Add the client's email in the Customer card first, then tap Send Proposal again."
                  );
                  onBack();
                  return;
                }
                setPreviewMode(true);
              }}
            >
              Send Proposal
            </button>
          </>
        )}
      </div>

      <MobileSendSheet
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        snapshot={snapshotForSend}
        pricingItems={pricingItems}
        estimate={estimate}
        orgId={orgId}
        userSettings={userSettings}
        proposalNotes={proposalNotes}
      />
    </>
  );
}
